import { z } from "zod";
import { okResponse } from "../auth/auth.schemas.js";
import { userRole } from "../users/users.schemas.js";
export const createInvitationRequest = z.object({
    email: z.string().email().max(255),
    // tenant_admin IS invitable, but only by a tenant_admin caller — enforced by
    // assertCanInvite (a store_manager's allowed set excludes it).
    role: userRole,
    /**
     * Public storeIds to bind the invitee to on accept.
     *   tenant-wide role  -> must be omitted or empty
     *   store-scoped role -> required, at least one storeId
     *   store_manager caller -> every storeId must be in their accessible set
     * Server enforces via assertCanInvite + tenant-membership check.
     */
    storeIds: z.array(z.string().uuid()).max(2000).default([]),
});
export const createInvitationResponse = okResponse.extend({
    invitationId: z.string().uuid(),
});
export const acceptInvitationResponse = z.object({
    ok: z.literal(true),
    tenantId: z.string().uuid(),
    // The joined tenant's URL code, so the client can hard-navigate straight INTO
    // that company (/t/<code>/dashboard) rather than SPA-navigating with a stale
    // companies cache and getting bounced to /onboarding.
    tenantCode: z.string().min(1),
});
// ── List + revoke pending invitations ────────────────────────────────
export const invitationSummary = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: userRole,
    storeIds: z.array(z.string().uuid()),
    invitedByEmail: z.string().email().nullable(),
    status: z.enum(["pending", "accepted", "cancelled", "expired"]),
    createdAt: z.string(),
    expiresAt: z.string(),
});
export const listInvitationsResponse = z.object({
    items: z.array(invitationSummary),
});
export const revokeInvitationResponse = okResponse;
// ── Reissue (resend) a pending invitation ────────────────────────────
// Mints a fresh token for an existing pending invite (the old one is
// invalidated), re-sends the email, AND returns the link so an admin can copy
// it directly — the raw token isn't stored, so this is the only way to recover
// a usable link after creation.
export const reissueInvitationResponse = okResponse.extend({
    invitationId: z.string().uuid(),
    inviteLink: z.string().url(),
    emailSent: z.boolean(),
});
// ── Public pre-flight lookup ────────────────────────────────────────
// Called by /accept-invitation BEFORE asking the user to log in or sign
// up, so we can render "expired" / "already accepted" / "you're being
// invited to {company} as {role}" without round-tripping through auth.
// The token itself is the credential — same threat model as the accept
// endpoint, no extra auth required.
export const invitationLookupQuery = z.object({
    token: z.string().min(16).max(255),
});
export const invitationLookupResponse = z.object({
    /**
     * Effective status:
     *   pending   — usable, awaiting accept.
     *   accepted  — already consumed; the recipient should sign in directly.
     *   cancelled — admin revoked it.
     *   expired   — past expires_at (server collapses "pending past expiry"
     *               into "expired" so the client only handles four states).
     */
    status: z.enum(["pending", "accepted", "cancelled", "expired"]),
    // Token holders can prefill authentication for a pending/accepted invite.
    // Unusable links omit the address; acceptance still enforces email binding.
    invitedEmail: z.string().email().nullable(),
    role: userRole,
    /** Human-readable store names (no IDs); empty for tenant-wide invites. */
    storeNames: z.array(z.string()),
    companyName: z.string(),
    invitedByEmail: z.string().email().nullable(),
    expiresAt: z.string(),
});
//# sourceMappingURL=invitations.schemas.js.map